import { useMemo, useRef, useState } from 'react'
import { fbt } from 'fbtee'
import { Check } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useEventsByIds } from '../hooks/useEventsByIds'
import { useGroups } from '../hooks/useGroups'
import { useGroupEvents, type GroupEventLinkEntryPoint } from '../hooks/useGroupEvents'
import { useTransitionNavigate } from '../lib/viewTransition'
import { CreateGroupDialog } from './CreateGroupDialog'
import { GroupUpsellCard } from './GroupUpsellCard'
import { LinkGroupList, PickGroupRow } from './LinkGroupList'
import { Skeleton } from './ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { placeholders } from '../lib/skeletonUtils'

/**
 * "Who are you going with" — the picker behind the post-RSVP toast's CTA and
 * the event Groups section's add button. It mounts only once opened, so the
 * group and groupEvent subscriptions it needs cost nothing until then.
 */
export function LinkGroupToEventDialog({
  eventId,
  onClose,
  entryPoint = 'rsvp_toast',
}: {
  eventId: string
  onClose: () => void
  entryPoint?: GroupEventLinkEntryPoint
}) {
  const navigate = useTransitionNavigate()
  const { user } = useAuth()
  const uid = user?.uid ?? null
  const { groups, loading, createGroup } = useGroups(uid)
  const { linkedGroups, linkGroup } = useGroupEvents(eventId, uid, groups)
  // Set overlap only means something at a festival; the event is already in
  // cache from the page or RSVP that opened this, so the copy doesn't flicker.
  const { events } = useEventsByIds(useMemo(() => [eventId], [eventId]))
  const isParty = events[0]?.type === 'party'
  const [pickerOpen, setPickerOpen] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  // The create dialog closes itself on submit, and that close must not be read
  // as "the user backed out" — the group it just made still has to be linked.
  const createdRef = useRef(false)
  // The freshly created group hasn't reached the groups subscription yet when
  // we link it, so its name has to come from the form that made it.
  const createdNameRef = useRef('')

  const linkedIds = new Set(linkedGroups.map(g => g.id))
  const unlinkedGroups = groups.filter(g => !linkedIds.has(g.id))

  async function link(groupId: string, nameHint?: string) {
    const name = groups.find(g => g.id === groupId)?.name ?? nameHint ?? ''
    setPickerOpen(false)
    // Closed before the write resolves, so the host can reopen the picker
    // straight away and a failed link (already toasted) doesn't leave it stuck.
    onClose()
    await linkGroup(groupId, entryPoint, name)
  }

  function openCreate() {
    createdRef.current = false
    setPickerOpen(false)
    setCreateOpen(true)
  }

  return (
    <>
      <Dialog
        open={pickerOpen}
        onOpenChange={next => {
          setPickerOpen(next)
          if (!next) onClose()
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <fbt desc="Title of the dialog that links a group to an event right after the user RSVPs">
                Who are you going with?
              </fbt>
            </DialogTitle>
            <DialogDescription>
              {isParty ? (
                <fbt desc="Description of the link-a-group dialog when the event is a party">
                  Link a group to this event and everyone in it can see who's in.
                </fbt>
              ) : (
                <fbt desc="Description of the dialog that links a group to an event right after the user RSVPs">
                  Link a group to this event and everyone in it can see who's in and
                  which sets overlap.
                </fbt>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex min-w-0 flex-col gap-1">
            {loading ? (
              placeholders(2).map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)
            ) : groups.length === 0 ? (
              <GroupUpsellCard onClick={openCreate} />
            ) : (
              <>
                {linkedGroups.map(group => (
                  <PickGroupRow
                    key={group.id}
                    group={group}
                    onClick={() => {
                      onClose()
                      navigate(`/groups/${group.id}`, { state: { entryPoint: 'event' } })
                    }}
                    trailing={
                      <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground">
                        <Check className="size-3.5" />
                        {fbt(
                          'Linked',
                          'Pill on a group row in the post-RSVP prompt for a group already linked to the event',
                        )}
                      </span>
                    }
                  />
                ))}
                <LinkGroupList
                  groups={unlinkedGroups}
                  viewerId={uid}
                  onLink={groupId => void link(groupId)}
                  onCreateGroup={openCreate}
                />
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <CreateGroupDialog
        open={createOpen}
        onOpenChange={next => {
          setCreateOpen(next)
          if (!next && !createdRef.current) onClose()
        }}
        onCreate={async (name, imageUrl) => {
          const id = await createGroup(name, imageUrl)
          createdRef.current = !!id
          createdNameRef.current = name
          return id
        }}
        onCreated={id => void link(id, createdNameRef.current)}
      />
    </>
  )
}
